import { type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/query/keys';
import { useAuth } from '@/lib/auth/useAuth';
import { collectionForApiKey, routeForApiKey } from '@/config/collections';
import { getLeadType } from '@/config/leadTypes';
import { workApi } from '@/features/work/api';
import { DataView } from '@/lib/ui/states';
import { Card } from '@/lib/ui/Card';
import { SpecBand } from '@/lib/ui/SpecBand';
import { PageHeader, Eyebrow, MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { Button } from '@/lib/ui/Button';
import { useToast } from '@/lib/ui';
import type { DashboardSummary } from '@/lib/api/types';
import type { Permission } from '@/lib/rbac/permissions';
import { formatBytes, formatRelative } from '@/lib/format';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * The dashboard `content` map keys come from the backend as table names (`press_items`,
 * `svet_documents`). Both of these resolve through `collectionForApiKey` /
 * `routeForApiKey` in the registry rather than by transforming the string here, which is
 * the QA F-11 fix: the console's own key for one collection is `documents` while its table
 * is `svet_documents`, so underscores-to-hyphens produced `svet-documents`, matched no
 * registry entry, and rendered a card labelled `svet documents` with no href — on the one
 * page whose job is to be the way in. The mapping now lives with the collection definition
 * and one lookup serves the route and the label, so the two cannot answer differently.
 *
 * A genuinely unknown key still renders as a non-navigable card with a humanized label.
 * That is the right defensive default for a collection this console has never heard of,
 * and it is what `collections.test.ts` asserts is no longer reached by any key the
 * dashboard actually sends.
 */
function collectionRoute(col: string): string | null {
  return routeForApiKey(col);
}

function collectionLabel(col: string, t: TFn): string {
  if (col === 'pages') return t('nav.pages');
  if (col === 'product_families' || col === 'product-families') return t('nav.productFamilies');
  const cfg = collectionForApiKey(col);
  return cfg ? t(cfg.label, { defaultValue: cfg.label }) : col.replace(/_/g, ' ');
}

/**
 * Humanize a wait in seconds for the publishing-queue panel. The queue's own
 * numbers are only half the signal: 3 pending messages is a queue draining
 * normally, 3 pending for six hours is a dispatcher that has stopped.
 */
function formatAge(seconds: number, t: TFn): string {
  if (seconds < 60) return t('dashboard.ageSeconds', { count: Math.round(seconds) });
  if (seconds < 3600) return t('dashboard.ageMinutes', { count: Math.round(seconds / 60) });
  if (seconds < 86_400) return t('dashboard.ageHours', { count: Math.round(seconds / 3600) });
  return t('dashboard.ageDays', { count: Math.round(seconds / 86_400) });
}

/** Localize an audit action/entity token, falling back to a humanized form. */
function localizeToken(kind: 'action' | 'entity', token: string, t: TFn): string {
  if (!token) return '';
  return t(`activity.${kind}.${token}`, { defaultValue: token.replace(/_/g, ' ') });
}

/**
 * The lead counters this role may act on.
 *
 * A card the role cannot open is not shown: since PHASE 01 of
 * PLAN_student-records-console the seminar applications sit behind
 * `students:read`, which only `admin` holds (D-S3). be-svet's dashboard Summary
 * already omits that key for anyone else, so this is the console's half of the
 * same rule — it also covers a summary served from cache before the re-gate.
 *
 * A key with no registry entry is kept, unlinked, exactly as the card renderer
 * already treated it: the dashboard renders defensively over whatever the API
 * sends, and hiding an unknown counter would hide a new lead type instead of
 * surfacing it.
 */
function visibleLeadCounts(
  leads: DashboardSummary['leads'] | undefined,
  can: (permission: Permission) => boolean,
): [string, { total: number; new: number }][] {
  return Object.entries(leads ?? {}).filter(([type]) => {
    const cfg = getLeadType(type);
    return !cfg || can(cfg.readPermission);
  });
}

/**
 * One item on the dashboard's attention band: something that is waiting on a
 * person, with the consequence of ignoring it and somewhere to go about it.
 */
interface Attention {
  id: string;
  tone: 'amber' | 'blue';
  title: string;
  body: string;
  to?: string;
  linkLabel?: string;
  action?: ReactNode;
}

/**
 * "What needs me?", derived from the summary the dashboard already fetches.
 *
 * The PHASE 07 critique's open question about this screen was what it would look
 * like if it answered that instead of listing counts, and the answer did not need
 * a new endpoint: two of the numbers already on the page are *states*, not
 * statistics. A dead outbox row means a record that reads "published" in here is
 * still serving the previous version on the public site — the one failure an
 * editor cannot see from anywhere else in the product. A lead with `new > 0` is a
 * person waiting for a reply. Everything else on this page is reference, and
 * reference belongs under the thing you came to find out.
 *
 * The band is deliberately allowed to be empty, and says so in words rather than
 * disappearing: "nothing is waiting" is an answer, and a section that vanishes
 * when it has nothing to say is indistinguishable from one that failed to load.
 */
function attentionItems(
  d: DashboardSummary,
  opts: {
    salesOnly: boolean;
    can: (p: Permission) => boolean;
    t: TFn;
    requeue: ReactNode;
    /** Everyone waiting on staff, across every cohort (`/admin/needs-action`'s totals). */
    needsAction?: number;
  },
): Attention[] {
  const { salesOnly, can, t, requeue, needsAction } = opts;
  const items: Attention[] = [];

  // Round 10, phase 24: people waiting on a decision, a reminder, a group. The
  // band carries the total and Needs action carries the reasons.
  if (!salesOnly && can('students:read') && needsAction && needsAction > 0) {
    items.push({
      id: 'needs-action',
      tone: 'amber',
      title: t('dashboard.attentionNeedsActionTitle', { count: needsAction }),
      body: t('dashboard.attentionNeedsActionBody'),
      to: '/needs-action',
      linkLabel: t('dashboard.attentionNeedsActionLink'),
    });
  }

  if (!salesOnly && d.outbox) {
    if (d.outbox.dead > 0) {
      items.push({
        id: 'outbox-dead',
        tone: 'amber',
        title: t('dashboard.attentionDeadTitle', { count: d.outbox.dead }),
        body: t('dashboard.outboxDeadHint', { count: d.outbox.dead }),
        action: can('settings:manage') ? requeue : undefined,
      });
    }
    // Fifteen minutes: the dispatcher's normal drain is seconds, so a message
    // this old is a stopped worker rather than a busy one. Below that threshold
    // the queue is reported in the reference band and not here — a dashboard
    // that cries about a queue doing its job teaches people to ignore it.
    if (d.outbox.pending > 0 && d.outbox.oldest_pending_age_seconds > 900) {
      items.push({
        id: 'outbox-stuck',
        tone: 'amber',
        title: t('dashboard.attentionStuckTitle', { count: d.outbox.pending }),
        body: t('dashboard.attentionStuckBody', {
          age: formatAge(d.outbox.oldest_pending_age_seconds, t),
        }),
      });
    }
  }

  // The server decides staleness (it holds the clock the heartbeat was written
  // with); this only words it. There is nothing to click: the fix is on the
  // server, so the body says who to tell rather than offering a link that
  // cannot help.
  if (!salesOnly && d.backup?.stale) {
    const { age_hours: ageHours, stale_after_hours: hours } = d.backup;
    items.push({
      id: 'backup-stale',
      tone: 'amber',
      title:
        ageHours == null
          ? t('dashboard.attentionBackupNeverTitle')
          : t('dashboard.attentionBackupStaleTitle', { hours }),
      body:
        ageHours == null
          ? t('dashboard.attentionBackupNeverBody')
          : t('dashboard.attentionBackupStaleBody', {
              // In hours for the first three days: under a title that counts in
              // hours, "31 hours" reads as the overdue run it is, "1 day" as on time.
              age:
                ageHours < 72
                  ? t('dashboard.ageHours', { count: Math.round(ageHours) })
                  : formatAge(ageHours * 3600, t),
            }),
    });
  }

  for (const [type, lead] of visibleLeadCounts(d.leads, can)) {
    const count = (lead && typeof lead === 'object' ? lead.new : 0) ?? 0;
    if (count <= 0) continue;
    const cfg = getLeadType(type);
    if (!cfg) continue;
    const label = t(`nav.${cfg.navKey}`);
    items.push({
      id: `lead-${type}`,
      tone: 'blue',
      title:
        type === 'newsletter'
          ? t('dashboard.attentionPendingSubs', { count })
          : t('dashboard.attentionNewLeads', { count, label }),
      body:
        type === 'newsletter'
          ? t('dashboard.attentionPendingSubsBody')
          : t('dashboard.attentionNewLeadsBody', { label: label.toLowerCase() }),
      to: type === 'newsletter' ? `/leads/${type}?status=pending` : `/leads/${type}?status=new`,
      linkLabel: t('dashboard.attentionOpen', { label }),
    });
  }

  return items;
}

function AttentionBand({ items, t }: { items: Attention[]; t: TFn }) {
  return (
    <section>
      <Eyebrow>{t('dashboard.attention')}</Eyebrow>
      {items.length === 0 ? (
        <Card className="mt-sm">
          <p className="text-component-title text-ink-deep">{t('dashboard.attentionNoneTitle')}</p>
          <p className="mt-2xs max-w-prose text-small text-ink-mid">
            {t('dashboard.attentionNoneBody')}
          </p>
        </Card>
      ) : (
        <ul className="mt-sm flex flex-col gap-sm">
          {items.map((item) => (
            <li key={item.id}>
              <Card
                className={item.tone === 'amber' ? 'border-signal-amber' : undefined}
              >
                <div className="flex flex-wrap items-start justify-between gap-md">
                  {/*
                    The measure sits on each text element, not on this wrapper:
                    `ch` resolves against the element's OWN font size, so a
                    measure set on a parent hands a `text-small` child ~108
                    characters a line — half again the measure it was given.
                    `max-w-prose` (65ch) is the console's one measure token;
                    the arbitrary `max-w-[70ch]` this file used measured ~86
                    characters in the console's own face.
                  */}
                  <div>
                    <p className="max-w-prose text-component-title text-ink-deep">{item.title}</p>
                    <p className="mt-2xs max-w-prose text-small text-ink-mid">{item.body}</p>
                  </div>
                  <div className="flex items-center gap-sm">
                    {item.action}
                    {item.to && (
                      <Link
                        to={item.to}
                        className="rounded-sm py-2xs text-small text-accent-blue underline-offset-4 hover:underline focus-visible:shadow-focus"
                      >
                        {item.linkLabel}
                      </Link>
                    )}
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Dashboard (spec §8). Renders defensively (iterate keys). Sales sees a leads-only variant. */
export function DashboardPage() {
  const { t } = useTranslation();
  const { role, can } = useAuth();
  const query = useQuery({
    queryKey: keys.dashboard(),
    queryFn: () => api.get<DashboardSummary>('/admin/dashboard'),
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
  const salesOnly = role === 'sales';
  // The Needs action total for the attention band: one row requested, the
  // per-reason totals summed (they cover every reason over no cohort filter).
  const needsAction = useQuery({
    queryKey: keys.needsAction.list({ summary: 1 }),
    queryFn: () => workApi.needsAction({ per_page: 1 }),
    enabled: !salesOnly && can('students:read'),
    staleTime: 15_000,
  });
  const needsActionTotal = needsAction.data
    ? Object.values(needsAction.data.totals).reduce((sum: number, n) => sum + (n ?? 0), 0)
    : undefined;
  const toast = useToast();
  const qc = useQueryClient();

  // Replaying the dead-letter queue re-fires real side effects (revalidations,
  // emails, third-party subscribes), so it is settings:manage on the server and
  // the button is hidden for everyone else rather than offered and refused.
  const retry = useMutation({
    mutationFn: () =>
      api.post<{ requeued: number; remaining_dead: number }>('/admin/outbox/requeue-dead', {}),
    onSuccess: (res) => {
      toast.success(
        t('dashboard.outboxRequeued', { count: res.requeued, remaining: res.remaining_dead }),
      );
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });

  // One definition, rendered in the attention band. Extracted so the control and
  // its permission gate cannot drift from the panel that used to own them.
  const requeueButton = (
    <Button
      variant="ghost"
      onClick={() => retry.mutate()}
      loading={retry.isPending}
      disabled={retry.isPending}
    >
      {retry.isPending ? t('dashboard.outboxRequeueBusy') : t('dashboard.outboxRequeue')}
    </Button>
  );

  return (
    <div>
      <PageHeader eyebrow={t('dashboard.eyebrow')} title={t('dashboard.title')} />
      <DataView
        query={query}
        copy={{ loading: t('dashboard.loading'), errorTitle: t('dashboard.loadFailed') }}
      >
        {(d) => (
          <div className="flex flex-col gap-lg">
            <AttentionBand
              items={attentionItems(d, { salesOnly, can, t, requeue: requeueButton, needsAction: needsActionTotal })}
              t={t}
            />

            {!salesOnly && (
              <>
                <section>
                  <Eyebrow>{t('dashboard.pages')}</Eyebrow>
                  <SpecBand
                    className="mt-sm"
                    items={[
                      { label: t('dashboard.total'), value: <MonoValue>{d.pages?.total ?? 0}</MonoValue> },
                      { label: t('dashboard.published'), value: <MonoValue>{d.pages?.published ?? 0}</MonoValue> },
                      { label: t('dashboard.draft'), value: <MonoValue>{d.pages?.draft ?? 0}</MonoValue> },
                      {
                        label: t('dashboard.media'),
                        value: <MonoValue>{d.media?.total_assets ?? 0}</MonoValue>,
                        hint: formatBytes(d.media?.total_size_bytes ?? 0),
                      },
                    ]}
                  />
                </section>

                <section>
                  <Eyebrow>{t('dashboard.contentByCollection')}</Eyebrow>
                  <div className="mt-sm grid gap-md sm:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(d.content ?? {}).map(([col, c]) => {
                      const route = collectionRoute(col);
                      const count: { total?: number; published?: number } =
                        c && typeof c === 'object' ? c : {};
                      const card = (
                        <Card interactive={!!route}>
                          <p className="font-mono text-label-sm uppercase tracking-[0.12em] text-ink-mute">
                            {collectionLabel(col, t)}
                          </p>
                          <p className="mt-xs text-title text-ink-deep">
                            <MonoValue>{count.published ?? 0}</MonoValue>
                            {typeof count.total === 'number' && (
                              <span className="text-small text-ink-mute">
                                {' '}
                                {t('dashboard.ofPublished', { total: count.total })}
                              </span>
                            )}
                          </p>
                        </Card>
                      );
                      return route ? (
                        <Link key={col} to={route}>
                          {card}
                        </Link>
                      ) : (
                        <div key={col}>{card}</div>
                      );
                    })}
                  </div>
                </section>
              </>
            )}

            {/* The publishing queue (B-12), as reference only. A revalidation that
                never reached the website leaves the record reading "published"
                in here while the public page still serves the previous version
                — but that case is now raised in the attention band above, where
                someone will see it. What is left here is the reassurance: the
                queue is healthy, and this is how far behind it is. Repeating the
                dead count in both places would teach people that the band is
                decoration. */}
            {!salesOnly && d.outbox && (
              <section>
                <Eyebrow>{t('dashboard.outbox')}</Eyebrow>
                {d.outbox.dead === 0 && (
                  <p className="mt-2xs text-small text-ink-mute">{t('dashboard.outboxHealthy')}</p>
                )}
                {d.outbox.pending > 0 && (
                  <p className="mt-2xs text-small text-ink-mute">
                    {t('dashboard.outboxPending')}: <MonoValue>{d.outbox.pending}</MonoValue>
                    {d.outbox.oldest_pending_age_seconds > 0 && (
                      <>
                        {' — '}
                        {t('dashboard.outboxOldest', {
                          age: formatAge(d.outbox.oldest_pending_age_seconds, t),
                        })}
                      </>
                    )}
                  </p>
                )}
              </section>
            )}

            <section>
              <Eyebrow>{t('dashboard.leads')}</Eyebrow>
              <div className="mt-sm grid gap-md sm:grid-cols-2 lg:grid-cols-3">
                {visibleLeadCounts(d.leads, can).map(([type, l]) => {
                  // Defend against the value not being an object (legacy flat-int shape).
                  const lead: { total?: number; new?: number } =
                    l && typeof l === 'object' ? l : {};
                  const cfg = getLeadType(type);
                  const newCount = lead.new ?? 0;
                  // Newsletter has no "new" pipeline; its "new" counts pending
                  // double-opt-in. The status value is `pending` — what
                  // `newsletter_subscribers.status` actually stores — so this
                  // deep-link lands on rows instead of an empty page.
                  const route = cfg
                    ? type === 'newsletter'
                      ? `/leads/${type}?status=pending`
                      : `/leads/${type}?status=new`
                    : null;
                  const label = cfg ? t(`nav.${cfg.navKey}`) : type.replace(/_/g, ' ');
                  const card = (
                    <Card interactive={!!route}>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-label-sm uppercase tracking-[0.12em] text-ink-mute">
                          {label}
                        </p>
                        {newCount > 0 && (
                          <span className="rounded-pill bg-accent-blue px-[8px] py-[2px] font-mono text-label text-canvas-paper">
                            {t('dashboard.newCount', { count: newCount })}
                          </span>
                        )}
                      </div>
                      <p className="mt-xs text-title text-ink-deep">
                        <MonoValue>{lead.total ?? 0}</MonoValue>
                        <span className="text-small text-ink-mute"> {t('dashboard.totalSuffix')}</span>
                      </p>
                    </Card>
                  );
                  return route ? (
                    <Link key={type} to={route}>
                      {card}
                    </Link>
                  ) : (
                    <div key={type}>{card}</div>
                  );
                })}
              </div>
            </section>

            {/* Agent oversight. The point of letting a machine client write is that a
                human can see what it did without going looking; a dedicated strip
                does that where a busy audit feed would bury it. Absent entirely
                when no agent has written, so it costs nothing to have. */}
            {d.agent_activity && d.agent_activity.length > 0 && (
              <section>
                <Eyebrow>{t('dashboard.agentActivity')}</Eyebrow>
                <p className="mt-2xs max-w-prose text-small text-ink-mute">
                  {t('dashboard.agentActivityHint')}
                </p>
                <Card className="mt-sm p-0">
                  <ul className="divide-y divide-hairline">
                    {d.agent_activity.slice(0, 8).map((a, i) => (
                      <li
                        key={a.id ?? `agent-${a.created_at}-${i}`}
                        className="flex flex-wrap items-center justify-between gap-xs px-lg py-sm text-small"
                      >
                        <span className="flex flex-wrap items-center gap-xs">
                          <Tag variant="amber">{a.api_token_name || t('dashboard.agentActor')}</Tag>
                          <span className="text-ink-mute">{localizeToken('action', a.action, t)}</span>
                          <span className="text-ink-mid">{localizeToken('entity', a.entity_type, t)}</span>
                        </span>
                        <span className="font-mono text-label-sm text-ink-mute">
                          {formatRelative(a.created_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
                <Link
                  to="/audit"
                  className="mt-xs inline-block text-small text-accent-blue underline-offset-4 hover:underline"
                >
                  {t('dashboard.agentActivityLink')}
                </Link>
              </section>
            )}

            {d.recent_activity && d.recent_activity.length > 0 && (
              <section>
                <Eyebrow>{t('dashboard.recentActivity')}</Eyebrow>
                <Card className="mt-sm p-0">
                  <ul className="divide-y divide-hairline">
                    {d.recent_activity.slice(0, 12).map((a, i) => (
                      <li
                        key={a.id ?? `${a.created_at}-${i}`}
                        className="flex items-center justify-between px-lg py-sm text-small"
                      >
                        <span>
                          {/*
                            `||`, not `??` (QA F-15). be-svet assembles this row as a
                            `map[string]any` with `coalesce(u.email,'')`, so an anonymous
                            actor — every public form submission, which is exactly what this
                            fallback was written for — arrives as an empty STRING and not as
                            null. `??` catches null and undefined only, so the fallback was
                            unreachable and the feed rendered `submit seminar application`
                            with a gap where every other row carries an address. An audit
                            feed exists to distinguish "a visitor did this" from "we failed
                            to record who did this", and a blank draws neither.
                          */}
                          <span className="font-medium text-ink-deep">
                            {a.actor_email || t('dashboard.systemActor')}
                          </span>{' '}
                          <span className="text-ink-mute">{localizeToken('action', a.action, t)}</span>{' '}
                          <span className="text-ink-mid">{localizeToken('entity', a.entity_type, t)}</span>
                        </span>
                        <span className="font-mono text-label-sm text-ink-mute">
                          {formatRelative(a.created_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </section>
            )}
          </div>
        )}
      </DataView>
    </div>
  );
}
